import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { poolAddress, poolIdentifier } from "../api/types";
import type { EvmDexKind, ChainId, PoolCandidate, TaskPoolRef } from "../api/types";
import { Loader2, Search, Plus, X, AlertTriangle } from "lucide-react";

const V4_NO_HOOKS = "0x0000000000000000000000000000000000000000";

function hasHooks(p: { hooks?: string }): boolean {
  return Boolean(p.hooks && p.hooks.toLowerCase() !== V4_NO_HOOKS);
}

// Human-readable labels for known quote mints. Anything not in this map is
// shown as a truncated address — the bot still uses the real mint at runtime;
// the symbol is just UI sugar.
const KNOWN_MINT_SYMBOLS: Record<string, string> = {
  // Solana
  So11111111111111111111111111111111111111112: "SOL",
  EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v: "USDC",
  Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB: "USDT",
  USD1ttGY1N17NEEHLmELoaybftRBUSErhqYiQzvEmuB: "USD1",
  "2b1kV6DkPAnxd5ixfnxCpjxmKwqjjaYmCZfHsFu24GXo": "PYUSD",
  HmBdm8vbisABUjkxms6ZUnoaXbfwFM6ymxShWfAENaoi: "EITHER",
  // EVM
  "0x4200000000000000000000000000000000000006": "WETH",
  "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2": "WETH",
  "0x0bd7d308f8e1639fab988df18a8011f41eacad73": "WETH",
  "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c": "WBNB",
  "0x5fc5360d0400a0fd4f2af552add042d716f1d168": "USDG",
  "0x55d398326f99059ff775485246999027b3197955": "USDT",
  "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d": "USDC",
  "0x3600000000000000000000000000000000000000": "USDC",
  "0x0000000000000000000000000000000000000000": "ETH",
};

function quoteLabel(mint: string | undefined, chain?: ChainId): string {
  if (!mint) return "—";
  if (chain === "arc" && mint.toLowerCase() === "0x0000000000000000000000000000000000000000") {
    return "USDC (native)";
  }
  const known = KNOWN_MINT_SYMBOLS[mint] ?? KNOWN_MINT_SYMBOLS[mint.toLowerCase()];
  if (known) return known;
  return `${mint.slice(0, 4)}…${mint.slice(-4)}`;
}

function isKnownMint(mint: string | undefined): boolean {
  if (!mint) return false;
  return Boolean(KNOWN_MINT_SYMBOLS[mint] ?? KNOWN_MINT_SYMBOLS[mint.toLowerCase()]);
}

export function PoolPicker({
  mint,
  chain,
  selected,
  onChange,
}: {
  mint: string;
  chain: ChainId;
  selected: TaskPoolRef[];
  onChange: (pools: TaskPoolRef[]) => void;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<PoolCandidate[]>([]);

  // EVM manual-entry inputs — kept as fallback alongside auto-discovery so a
  // user can paste a known pool address that DexScreener hasn't indexed yet
  // (fresh launches, niche forks). Default DEX changes per chain.
  const [evmAddr, setEvmAddr] = useState("");
  const isEvm = chain === "base" || chain === "ethereum" || chain === "robinhood" || chain === "bnb" || chain === "ink" || chain === "arc";
  const evmDexOptions: { value: EvmDexKind; label: string }[] =
    chain === "ink"
      ? [{ value: "uniswap_v2", label: "InkySwap V1 / Uniswap V2" }]
      : chain === "arc"
      ? [{ value: "uniswap_v3", label: "Lift V1 / Uniswap V3" }]
      : chain === "robinhood"
      ? [
          { value: "pons_curve", label: "Pons bonding curve" },
          { value: "uniswap_v3", label: "Uniswap V3" },
          { value: "uniswap_v2", label: "Uniswap V2" },
          { value: "uniswap_v4", label: "Uniswap V4" },
        ]
      : chain === "bnb"
      ? [
          { value: "pancakeswap_v3", label: "PancakeSwap V3" },
          { value: "pancakeswap_v2", label: "PancakeSwap V2" },
          { value: "uniswap_v3", label: "Uniswap V3" },
        ]
      : chain === "ethereum"
      ? [
          { value: "uniswap_v3", label: "Uniswap V3" },
          { value: "uniswap_v2", label: "Uniswap V2" },
          { value: "sushiswap", label: "SushiSwap" },
          { value: "uniswap_v4", label: "Uniswap V4" },
        ]
      : [
          { value: "uniswap_v3", label: "Uniswap V3" },
          { value: "uniswap_v2", label: "Uniswap V2" },
          { value: "aerodrome", label: "Aerodrome" },
          { value: "uniswap_v4", label: "Uniswap V4" },
        ];
  const [evmDex, setEvmDex] = useState<EvmDexKind>(evmDexOptions[0]!.value);

  useEffect(() => {
    if (!evmDexOptions.some((o) => o.value === evmDex)) {
      setEvmDex(evmDexOptions[0]!.value);
    }
  }, [evmDex, evmDexOptions]);

  async function runDiscover(): Promise<void> {
    if (!mint || mint.length < 10) {
      setError("Enter a valid mint first");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ pools: PoolCandidate[] }>("/api/pools/discover", {
        mint,
        chain,
      });
      setCandidates(res.pools);
      if (res.pools.length === 0) setError("No pools found for this mint");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Discover failed");
    } finally {
      setLoading(false);
    }
  }

  function toggle(p: PoolCandidate): void {
    // V4 entries identify by poolId (PoolManager is singleton); others by
    // pool contract address.
    const isV4 = p.dex === "uniswap_v4" || p.dex === "lift_v2" || p.dex === "tsunami_v4";
    const isInkyCurve = p.dex === "inkypump_v2";
    const isFullFunCurve = p.dex === "full_fun";
    const candidateKey = isV4
      ? p.poolId ?? p.poolAccount
      : isInkyCurve
        ? p.launchId ?? p.poolAccount
        : isFullFunCurve
          ? p.fullFunPoolId ?? p.poolAccount
        : p.poolAccount;
    const exists = selected.find(
      (s) => poolIdentifier(s).toLowerCase() === candidateKey.toLowerCase(),
    );
    if (exists) {
      onChange(
        selected.filter(
          (s) => poolIdentifier(s).toLowerCase() !== candidateKey.toLowerCase(),
        ),
      );
    } else {
      const ref: TaskPoolRef = { account: p.poolAccount, dex: p.dex };
      if (isV4 && p.poolId) {
        ref.poolId = p.poolId;
        // For V4, `account` is the poolId (no per-pool contract address).
        // Storage normalizer fills the chain's PoolManager address later;
        // we just need to keep poolId on the ref for registry seeding.
        if (p.currency0 && p.currency1 && p.fee !== undefined &&
          p.tickSpacing !== undefined && p.hooks) {
          ref.currency0 = p.currency0;
          ref.currency1 = p.currency1;
          ref.fee = p.fee;
          ref.tickSpacing = p.tickSpacing;
          ref.hooks = p.hooks;
        }
      }
      if (isInkyCurve && p.launchId) ref.launchId = p.launchId;
      if (isFullFunCurve && p.fullFunPoolId) ref.fullFunPoolId = p.fullFunPoolId;
      // Preserve the verified quote for settlement controls. Solana uses it
      // at runtime; EVM uses it to suggest an exact WETH/quote bridge.
      if (p.quoteMint) {
        ref.quoteMint = p.quoteMint;
      }
      onChange([...selected, ref]);
    }
  }

  async function addManualEvm(): Promise<void> {
    const a = evmAddr.trim();
    if (evmDex === "uniswap_v4" || evmDex === "lift_v2") {
      if (!/^0x[a-fA-F0-9]{64}$/.test(a)) {
        setError("V4 Pool ID must be 0x-prefixed 64-hex");
        return;
      }
      if (!/^0x[a-fA-F0-9]{40}$/.test(mint.trim())) {
        setError("Enter a valid token contract first");
        return;
      }
      if (selected.some((s) => poolIdentifier(s).toLowerCase() === a.toLowerCase())) {
        setError("Pool already added");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await api.post<{ pools: PoolCandidate[] }>("/api/pools/discover", {
          mint,
          chain,
          poolId: a,
        });
        const pool = res.pools[0];
        if (!pool) {
          setError("V4 Pool ID was not found or could not be verified");
          return;
        }
        toggle(pool);
        setEvmAddr("");
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "V4 Pool ID verification failed");
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(a)) {
      setError("Pool address must be 0x-prefixed 40-hex");
      return;
    }
    if (selected.some((s) => poolAddress(s).toLowerCase() === a.toLowerCase())) {
      setError("Pool already added");
      return;
    }
    onChange([...selected, { account: a.toLowerCase(), dex: evmDex }]);
    setEvmAddr("");
    setError(null);
  }

  function removeSelected(addr: string): void {
    onChange(
      selected.filter((s) => poolIdentifier(s).toLowerCase() !== addr.toLowerCase()),
    );
  }

  function fmtUsd(n: number | null): string {
    if (n === null) return "—";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n.toFixed(0)}`;
  }

  // Discovery UI is identical across all chains now (Solana, Base, Ethereum).
  // EVM chains also expose a manual-entry row underneath the discovery list
  // for fresh tokens not yet indexed on DexScreener.
  const explorerHost =
    chain === "ethereum"
      ? "etherscan.io"
      : chain === "base"
        ? "basescan.org"
        : chain === "robinhood"
          ? "robinhoodchain.blockscout.com"
          : chain === "bnb"
            ? "bscscan.com"
            : chain === "ink"
              ? "explorer.inkonchain.com"
              : chain === "arc"
                ? "arc-scan.org"
              : null;
  const dsHost =
    chain === "ethereum"
      ? "ethereum"
      : chain === "base"
        ? "base"
        : chain === "robinhood"
          ? "robinhood"
          : chain === "bnb"
            ? "bsc"
            : chain === "ink"
              ? "ink"
              : chain === "arc"
                ? "arc"
              : "solana";

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          onClick={() => void runDiscover()}
          disabled={loading || !mint}
          className="btn btn-ghost disabled:opacity-40"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} strokeWidth={1.5} />}
          Discover pools
        </button>
        <span className="text-xs text-muted">
          {selected.length > 0 ? `${selected.length} selected` : "None selected"}
        </span>
      </div>
      {error && <div className="text-xs text-err mb-3">{error}</div>}
      {candidates.length > 0 && (
        <div className="border border-border mb-3">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="bg-hover">
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left">DEX</th>
                <th className="px-3 py-2 text-left">Pool</th>
                <th
                  className="px-3 py-2 text-left"
                  title="Quote token — we'll pay/receive in this currency. Default WSOL if pool is paired with native SOL."
                >
                  Quote
                </th>
                <th className="px-3 py-2 text-right">Liquidity</th>
                <th className="px-3 py-2 text-right">Vol 24h</th>
                <th className="px-3 py-2 text-right">Price</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((p) => {
                const candidateKey =
                  p.dex === "uniswap_v4" || p.dex === "lift_v2" || p.dex === "tsunami_v4"
                    ? (p.poolId ?? p.poolAccount).toLowerCase()
                    : p.dex === "inkypump_v2"
                      ? (p.launchId ?? p.poolAccount).toLowerCase()
                      : p.dex === "full_fun"
                        ? (p.fullFunPoolId ?? p.poolAccount).toLowerCase()
                      : p.poolAccount.toLowerCase();
                const checked = !!selected.find(
                  (s) => poolIdentifier(s).toLowerCase() === candidateKey,
                );
                const hooksOn = hasHooks(p);
                return (
                  <tr
                    key={p.poolAccount}
                    className="border-t border-border hover:bg-hover cursor-pointer"
                    onClick={() => toggle(p)}
                  >
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={checked} readOnly />
                    </td>
                    <td className="px-3 py-2">
                      <span>{p.dex}</span>
                      {p.fee !== undefined && (
                        <span className="text-muted ml-1">
                          {p.fee === 0x800000 ? "dynamic" : `${(p.fee / 10000).toFixed(2)}%`}
                        </span>
                      )}
                      {hooksOn && (
                        <span
                          className="inline-flex items-center gap-0.5 ml-2 px-1 py-0.5 text-[9px] uppercase tracking-nav border border-warn text-warn"
                          title={p.dex === "tsunami_v4" ? `Verified Tsunami hook: ${p.hooks}. Fees are included in the onchain quote.` : p.dex === "lift_v2" ? `Verified Lift V2 LaunchHook: ${p.hooks}. Discovery and task start both verify the factory launch and PoolKey.` : `This V4 pool has a hooks contract at ${p.hooks}. Hooks can override the swap math (dynamic fees, custom logic).`}
                        >
                          <AlertTriangle size={9} strokeWidth={2} />
                          hooks
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {p.poolAccount.slice(0, 8)}…{p.poolAccount.slice(-4)}
                    </td>
                    <td
                      className="px-3 py-2"
                      title={p.quoteMint ?? "no quote mint reported by discovery"}
                    >
                      <span
                        className={
                          isKnownMint(p.quoteMint)
                            ? "text-fg"
                            : "text-warn"
                        }
                      >
                        {quoteLabel(p.quoteMint, chain)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{fmtUsd(p.liquidityUsd)}</td>
                    <td className="px-3 py-2 text-right">{fmtUsd(p.volume24hUsd)}</td>
                    <td className="px-3 py-2 text-right">
                      {p.priceUsd !== null ? `$${p.priceUsd.toExponential(2)}` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isEvm && (
        <>
          {selected.length > 0 && candidates.length === 0 && (
            <div className="border border-border mb-3">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="bg-hover">
                    <th className="px-3 py-2 text-left">DEX</th>
                    <th className="px-3 py-2 text-left">Pool</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {selected.map((p) => {
                    const id = poolIdentifier(p);
                    return (
                      <tr key={id} className="border-t border-border">
                        <td className="px-3 py-2">{p.dex}</td>
                        <td className="px-3 py-2">
                          {id.slice(0, 8)}…{id.slice(-4)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => removeSelected(id)}
                            className="text-muted hover:text-err"
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex gap-2 mb-2">
            <input
              className="input font-mono text-xs flex-1"
              value={evmAddr}
              onChange={(e) => setEvmAddr(e.target.value)}
              placeholder={
                evmDex === "uniswap_v4" || evmDex === "lift_v2"
                  ? "Paste V4 Pool ID (0x... 64 hex)"
                  : "Or paste pool address (0x... 40 hex)"
              }
            />
            <select
              className="input w-40"
              value={evmDex}
              onChange={(e) => setEvmDex(e.target.value as EvmDexKind)}
            >
              {evmDexOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => void addManualEvm()} disabled={loading} className="btn btn-ghost disabled:opacity-40">
              <Plus size={14} strokeWidth={1.5} />
              Add
            </button>
          </div>
          <div className="text-xs text-muted">
            Tip: find pool on{" "}
            {explorerHost && (
              <>
                <a
                  href={chain === "arc" ? `https://arc-scan.org/address/${mint || "0x"}` : `https://${explorerHost}/token/${mint || "0x"}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {explorerHost.replace(".org", "").replace(".io", "")}
                </a>{" "}
                or{" "}
              </>
            )}
            <a
              href={chain === "arc" ? `https://www.full.fun/arc/${mint || ""}` : `https://dexscreener.com/${dsHost}/${mint || ""}`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              {chain === "arc" ? "Full.fun / Lift.fun" : "DexScreener"}
            </a>
          </div>
        </>
      )}
    </div>
  );
}
